import React from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Calendar, User } from "lucide-react";
import api, { MEDIA } from "../lib/api";
import { useSettings } from "../contexts/SettingsContext";

function setMeta(name, content) {
    let el = document.querySelector(`meta[name="${name}"]`) || document.querySelector(`meta[property="${name}"]`);
    if (!el) {
        el = document.createElement("meta");
        el.setAttribute(name.startsWith("og:") || name.startsWith("twitter:") ? "property" : "name", name);
        document.head.appendChild(el);
    }
    el.setAttribute("content", content);
}
function removeMeta(name) {
    const el = document.querySelector(`meta[name="${name}"]`) || document.querySelector(`meta[property="${name}"]`);
    if (el) el.remove();
}

export default function BlogPost() {
    const { slug } = useParams();
    const [post, setPost] = React.useState(null);
    const { settings } = useSettings();

    React.useEffect(() => {
        api.get(`/blog/${slug}`).then(r => setPost(r.data)).catch(() => setPost(null));
    }, [slug]);

    React.useEffect(() => {
        if (!post) return;

        const title = post.seo_title || post.title;
        const description = post.seo_description || post.excerpt || "";
        const image = post.image ? (post.image.startsWith("http") ? post.image : MEDIA(post.image)) : "";
        const url = `${settings.site_base_url || window.location.origin}/blog/${post.slug}`;
        const siteName = settings.site_name || settings.company_name || "";

        // Page title
        document.title = title + (siteName ? ` | ${siteName}` : "");

        // Robots
        if (post.no_index) {
            setMeta("robots", "noindex, nofollow");
        } else {
            removeMeta("robots");
        }

        // Open Graph
        setMeta("og:type", "article");
        setMeta("og:title", title);
        if (description) setMeta("og:description", description);
        setMeta("og:url", url);
        if (image) setMeta("og:image", image);
        if (siteName) setMeta("og:site_name", siteName);

        // Twitter Card
        setMeta("twitter:card", image ? "summary_large_image" : "summary");
        setMeta("twitter:title", title);
        if (description) setMeta("twitter:description", description);
        if (image) setMeta("twitter:image", image);

        return () => {
            // Reset on unmount
            document.title = siteName || "ccashop";
            removeMeta("robots");
        };
    }, [post, settings]);

    if (!post) return (
        <div className="container-x py-20 max-w-3xl">
            <div className="space-y-4 animate-pulse">
                <div className="skeleton h-4 w-24 rounded-full" />
                <div className="skeleton h-10 w-full rounded-xl" />
                <div className="skeleton h-10 w-3/4 rounded-xl" />
                <div className="skeleton aspect-[16/9] w-full rounded-2xl" />
            </div>
        </div>
    );

    return (
        <article className="container-x py-8 sm:py-12 max-w-3xl animate-fade-up" data-testid="blog-post">
            <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors mb-8">
                <ArrowLeft className="h-4 w-4" /> Înapoi la blog
            </Link>

            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-4">
                <div className="flex items-center gap-1.5">
                    <Calendar className="h-3.5 w-3.5" />
                    {new Date(post.created_at).toLocaleDateString("ro-RO", { day: "numeric", month: "long", year: "numeric" })}
                </div>
                {post.author && (
                    <div className="flex items-center gap-1.5">
                        <User className="h-3.5 w-3.5" />
                        {post.author}
                    </div>
                )}
                {post.category && (
                    <Link to={`/blog?cat=${encodeURIComponent(post.category)}`}
                        className="px-2.5 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/20 transition-colors">
                        {post.category}
                    </Link>
                )}
                {post.no_index && (
                    <span className="px-2 py-0.5 rounded-full bg-muted text-muted-foreground text-xs">noindex</span>
                )}
            </div>

            <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight leading-tight mb-6">
                {post.title}
            </h1>

            {post.image && (
                <div className="rounded-2xl overflow-hidden mb-8 aspect-[16/9] bg-muted">
                    <img src={MEDIA(post.image)} alt={post.title} className="w-full h-full object-cover" />
                </div>
            )}

            {post.excerpt && (
                <p className="text-lg text-muted-foreground border-l-4 border-primary pl-4 mb-8 italic">
                    {post.excerpt}
                </p>
            )}

            <div className="prose prose-neutral max-w-none text-foreground/80 leading-relaxed text-base whitespace-pre-line">
                {post.content}
            </div>

            <div className="mt-12 pt-8 border-t border-border">
                <Link to="/blog" className="btn-outline">
                    ← Toate articolele
                </Link>
            </div>
        </article>
    );
}
